import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import supabase from '../supabaseClient';
import './DirectoryPage.css';

const DirectoryPage = () => {
  const { member } = useAuth();
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const { data, error } = await supabase
          .from('members')
          .select('*')
          .order('name', { ascending: true });

        if (error) throw error;
        setMembers(data || []);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  const filteredMembers = members.filter((m) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      m.name.toLowerCase().includes(searchLower) ||
      (m.company && m.company.toLowerCase().includes(searchLower)) ||
      (m.role && m.role.toLowerCase().includes(searchLower))
    );
  });

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const handleAvatarUpload = async (e) => {
    if (!selectedMember || !member?.is_admin) return;

    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);

      // Resize image to 300x300
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = async () => {
        canvas.width = 300;
        canvas.height = 300;
        ctx.drawImage(img, 0, 0, 300, 300);

        canvas.toBlob(async (blob) => {
          try {
            const response = await fetch('/.netlify/functions/admin-upload-avatar', {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                member_id: selectedMember.id,
                image: await blobToBase64(blob),
              }),
            });

            if (response.ok) {
              const data = await response.json();
              setSelectedMember({ ...selectedMember, avatar_url: data.avatar_url });
              setMembers(
                members.map((m) =>
                  m.id === selectedMember.id ? { ...m, avatar_url: data.avatar_url } : m
                )
              );
            } else {
              alert('Erreur lors du téléchargement de l\'avatar');
            }
          } finally {
            setUploadingAvatar(false);
          }
        }, 'image/jpeg', 0.9);
      };

      img.src = URL.createObjectURL(file);
    } catch (error) {
      console.error('Avatar upload error:', error);
      setUploadingAvatar(false);
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(blob);
    });
  };

  if (loading) {
    return (
      <div className="directory-page">
        <div className="directory-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="directory-page">
      <div className="directory-container">
        <h1 className="directory-title">L'Annuaire</h1>

        <div className="search-container">
          <input
            type="text"
            placeholder="Rechercher par nom, entreprise ou fonction..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {filteredMembers.length > 0 ? (
          <div className="members-grid">
            {filteredMembers.map((m) => (
              <div
                key={m.id}
                className="member-card"
                onClick={() => setSelectedMember(m)}
              >
                {m.avatar_url ? (
                  <img src={m.avatar_url} alt={m.name} className="member-avatar" />
                ) : (
                  <div className="member-initials">{getInitials(m.name)}</div>
                )}
                <div className="member-card-info">
                  <h4>{m.name}</h4>
                  {m.company && <p>{m.company}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-results">Aucun membre ne correspond à votre recherche.</p>
        )}
      </div>

      {/* Member Profile Modal */}
      {selectedMember && (
        <div className="modal-overlay" onClick={() => setSelectedMember(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedMember(null)}>×</button>
            <div className="modal-body">
              <div className="avatar-container">
                {selectedMember.avatar_url ? (
                  <img src={selectedMember.avatar_url} alt={selectedMember.name} className="modal-avatar" />
                ) : (
                  <div className="modal-initials">{getInitials(selectedMember.name)}</div>
                )}
                {member?.is_admin && (
                  <label className="change-photo-label">
                    Changer la photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      disabled={uploadingAvatar}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
              <h2>{selectedMember.name}</h2>
              {selectedMember.company && <p className="modal-company">{selectedMember.company}</p>}
              {selectedMember.role && <p className="modal-role">{selectedMember.role}</p>}
              {selectedMember.bio && <p className="modal-bio">{selectedMember.bio}</p>}
              {selectedMember.linkedin && (
                <a href={selectedMember.linkedin} target="_blank" rel="noopener noreferrer" className="linkedin-button">
                  Voir sur LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #1f5d5b;
          border-top: 4px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DirectoryPage;
